import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    CircularProgress,
    Alert,
    Box,
    Typography,
    Chip,
    useTheme,
    useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { getOperationGeometries } from '../network/apis/missionlog.api';
import { useAuth } from '../context/authContext';

// Initialize Mapbox token
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_KEY;

/**
 * MapViewDialog Component
 * Displays operation and authorization geometries on a map
 * Reuses the same map styles and appearance as Home.js
 */
const MapViewDialog = ({ open, onClose, operationId, operationName }) => {
    const { token } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const mapContainer = useRef(null);
    const mapRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [geometryData, setGeometryData] = useState(null);

    // Get map style from environment (same as Home.js)
    const mapArray = process.env.REACT_APP_MAP_STYLES
        ? JSON.parse(process.env.REACT_APP_MAP_STYLES)
        : ['mapbox://styles/mapbox/satellite-streets-v12'];
    const mapStyle = mapArray[0];

    // Fetch geometry data when dialog opens
    useEffect(() => {
        if (!open || !operationId || !token) return;

        const fetchGeometries = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await getOperationGeometries(token, operationId);
                setGeometryData(data);
            } catch (err) {
                setError(err.response?.data?.error || 'Failed to load map data');
            } finally {
                setLoading(false);
            }
        };

        fetchGeometries();
    }, [open, operationId, token]);

    // Initialize map and render geometries
    useEffect(() => {
        if (!open || !geometryData || !mapContainer.current || mapRef.current) return;

        try {
            // Calculate center from operation geometry
            const operationGeom = geometryData.operationGeometry;
            let center = [-98.5795, 39.8283]; // Default US center

            if (operationGeom && operationGeom.coordinates && operationGeom.coordinates[0]) {
                const coords = operationGeom.coordinates[0];
                const lngs = coords.map(c => c[0]);
                const lats = coords.map(c => c[1]);
                center = [
                    (Math.min(...lngs) + Math.max(...lngs)) / 2,
                    (Math.min(...lats) + Math.max(...lats)) / 2
                ];
            }

            // Initialize map
            const map = new mapboxgl.Map({
                container: mapContainer.current,
                style: mapStyle,
                center: center,
                zoom: 14,
                attributionControl: false
            });

            mapRef.current = map;

            map.on('load', () => {
                // Add operation geometry layer (blue)
                if (operationGeom) {
                    map.addSource('operation-area', {
                        type: 'geojson',
                        data: {
                            type: 'Feature',
                            geometry: operationGeom
                        }
                    });

                    map.addLayer({
                        id: 'operation-area-fill',
                        type: 'fill',
                        source: 'operation-area',
                        paint: {
                            'fill-color': '#088',
                            'fill-opacity': 0.2
                        }
                    });

                    map.addLayer({
                        id: 'operation-area-outline',
                        type: 'line',
                        source: 'operation-area',
                        paint: {
                            'line-color': '#088',
                            'line-width': 2
                        }
                    });
                }

                // Add authorization geometries (different colors for each)
                const authColors = ['#FF6B6B', '#4ECDC4', '#FFE66D', '#A8E6CF', '#FF8B94'];

                geometryData.authorizations.forEach((auth, index) => {
                    if (!auth.authorizationGeometry) return;

                    const sourceId = `auth-${auth.authorizationId}`;
                    const color = authColors[index % authColors.length];

                    // Handle both FeatureCollection and plain Geometry types
                    let geoJsonData;
                    if (auth.authorizationGeometry.type === 'FeatureCollection') {
                        // Already a FeatureCollection, use as-is
                        geoJsonData = auth.authorizationGeometry;
                    } else if (auth.authorizationGeometry.type === 'Feature') {
                        // Single Feature, wrap in FeatureCollection
                        geoJsonData = {
                            type: 'FeatureCollection',
                            features: [auth.authorizationGeometry]
                        };
                    } else {
                        // Plain geometry (Polygon, MultiPolygon, etc), wrap in Feature
                        geoJsonData = {
                            type: 'Feature',
                            geometry: auth.authorizationGeometry,
                            properties: {
                                authorizationId: auth.authorizationId,
                                status: auth.status,
                                airport: auth.authorizingAirport,
                                maxAltitude: auth.maxAltitude
                            }
                        };
                    }

                    map.addSource(sourceId, {
                        type: 'geojson',
                        data: geoJsonData
                    });

                    map.addLayer({
                        id: `${sourceId}-fill`,
                        type: 'fill',
                        source: sourceId,
                        paint: {
                            'fill-color': color,
                            'fill-opacity': 0.3
                        }
                    });

                    map.addLayer({
                        id: `${sourceId}-outline`,
                        type: 'line',
                        source: sourceId,
                        paint: {
                            'line-color': color,
                            'line-width': 2
                        }
                    });

                    // Add click handler for authorization info (use closure to capture auth data)
                    map.on('click', `${sourceId}-fill`, (e) => {
                        new mapboxgl.Popup()
                            .setLngLat(e.lngLat)
                            .setHTML(`
                                <div style="padding: 8px;">
                                    <strong>Authorization ${auth.authorizationId}</strong><br/>
                                    Status: ${auth.status}<br/>
                                    Airport: ${auth.authorizingAirport}<br/>
                                    Max Altitude: ${auth.maxAltitude}ft
                                </div>
                            `)
                            .addTo(map);
                    });

                    // Change cursor on hover
                    map.on('mouseenter', `${sourceId}-fill`, () => {
                        map.getCanvas().style.cursor = 'pointer';
                    });

                    map.on('mouseleave', `${sourceId}-fill`, () => {
                        map.getCanvas().style.cursor = '';
                    });
                });

                // Fit bounds to show all geometries
                if (operationGeom) {
                    const coords = operationGeom.coordinates[0];
                    const bounds = coords.reduce((bounds, coord) => {
                        return bounds.extend(coord);
                    }, new mapboxgl.LngLatBounds(coords[0], coords[0]));

                    map.fitBounds(bounds, {
                        padding: isMobile ? 40 : 80,
                        maxZoom: 15
                    });
                }
            });

        } catch (err) {
            setError('Failed to initialize map');
        }

        // Cleanup
        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, [open, geometryData, mapStyle, isMobile]);

    const handleClose = () => {
        if (mapRef.current) {
            mapRef.current.remove();
            mapRef.current = null;
        }
        setGeometryData(null);
        setError(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    height: isMobile ? '100%' : '80vh',
                    maxHeight: isMobile ? '100%' : '90vh'
                }
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="h6" component="span">
                        Operation Map View (Beta)
                    </Typography>
                    {operationName && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {operationName}
                        </Typography>
                    )}
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress />
                    </Box>
                )}

                {error && (
                    <Box sx={{ p: 2 }}>
                        <Alert severity="error">{error}</Alert>
                    </Box>
                )}

                {!loading && !error && geometryData && (
                    <>
                        {/* Legend */}
                        <Box sx={{ p: 2, pb: 1, backgroundColor: 'rgba(255,255,255,0.95)', zIndex: 1 }}>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Chip
                                    label="Operation Area"
                                    size="small"
                                    sx={{
                                        backgroundColor: '#088',
                                        color: 'white',
                                        fontWeight: 500
                                    }}
                                />
                                {geometryData.authorizations.length > 0 && (
                                    <Chip
                                        label={`${geometryData.authorizations.length} Authorization(s)`}
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                                <Typography variant="caption" color="text.secondary">
                                    Max Altitude: {geometryData.maxAltitude}ft
                                </Typography>
                            </Box>
                        </Box>

                        {/* Map Container */}
                        <Box
                            ref={mapContainer}
                            sx={{
                                flex: 1,
                                width: '100%',
                                position: 'relative'
                            }}
                        />
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default MapViewDialog;
